/**
 * THE LIVE DEMO'S WAY IN, IN TODAY'S DESIGN (the public demo, exception M1; docs/PLAN-public-demo-20261030.md §9): what
 * today's header, home header and footer need to offer "try the live demo" — whether to offer it at all, the press that
 * opens the chooser, and the chooser itself.
 *
 * Offered only where the build's lever (REACT_APP_DEMO_INDUSTRIES) lets an industry in: with none, this answers null,
 * nothing is drawn and nothing of the demo is fetched. The chooser (./DemoChooserDialog.tsx) is its own chunk,
 * `demo-chooser`, whose one import() is here: it is fetched when a visitor reaches for the action (a pointer over it,
 * focus on it, a press), so this module, on the first download with today's header, holds only the press and a glyph.
 */
import React from 'react';
import { createOnDemandModule } from '../../utils/onDemandModule';
import { demoChoices } from '../../demo/demoAddress';
import type { DemoIndustryId } from '../../demo/demoIndustries';
// A TYPE-only import: erased at build, never an edge into the chunk.
import type * as DemoChooserDialogNamespace from './DemoChooserDialog';

const chooser = createOnDemandModule<typeof DemoChooserDialogNamespace>(
  () => import(/* webpackChunkName: "demo-chooser" */ './DemoChooserDialog'),
);

export interface DemoChooserEntry {
  /** The industries offered, in their order. */
  choices: readonly DemoIndustryId[];
  /** Open the chooser (it shows the moment its chunk is here). */
  open: () => void;
  /** A visitor reached for the action: fetch the chooser now. */
  reach: () => void;
  /** The chooser, to draw once anywhere in the caller (it portals to the page's body); null until it has arrived. */
  dialog: React.ReactNode;
}

/** "try the live demo"'s glyph in today's design: a pointer (the app to try, not a video to watch). */
export const DEMO_ENTRY_GLYPH = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m3 3 7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
    <path d="m13 13 6 6" />
  </svg>
);

/**
 * The way in where the lever lets an industry in, else null (draw nothing). Each caller has its own chooser; a press
 * after a dropped download asks again, so it still opens.
 */
export function useDemoChooser(): DemoChooserEntry | null {
  const choices = React.useMemo(() => demoChoices(), []);
  const [wanted, setWanted] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const mod = chooser.useModule(wanted && choices.length > 0, true);
  const close = React.useCallback(() => setOpen(false), []);
  const reach = React.useCallback(() => {
    setWanted(true);
    chooser.load().catch(() => undefined);
  }, []);
  const press = React.useCallback(() => {
    reach();
    setOpen(true);
  }, [reach]);
  if (choices.length === 0) return null;
  const Dialog = mod?.DemoChooserDialog;
  return {
    choices,
    open: press,
    reach,
    dialog: Dialog ? <Dialog open={open} onClose={close} choices={choices} /> : null,
  };
}

/** Tests only: forget the download. */
export const resetDemoChooserForTests = chooser.resetForTests;
